import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Alert, Button, Checkbox, Input, Password, goldToast } from "gold-ui-kit";
import { loginUser, verifyTwoFactor, clearError } from "../store/slices/authSlice";
import { useAuth } from "../context/AuthContext";
import { Lock, User, ArrowRight, Eye, EyeOff, ArrowLeft, ShieldCheck } from "lucide-react";

const FieldLabel = ({ htmlFor, children }) => (
  <label htmlFor={htmlFor} className="mb-1.5 block text-xs font-semibold text-slate-700">
    {children}
  </label>
);

const LoginPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error, successMessage, requiresTwoFactor, tempUserId } = useSelector((s) => s.auth);
  const { isLogged } = useAuth();

  const [username, setUsername] = useState(() => {
    try {
      return localStorage.getItem("remember_username") || "";
    } catch {
      return "";
    }
  });
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [rememberMe, setRememberMe] = useState(() => {
    try {
      return localStorage.getItem("remember_me") === "true";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (isLogged) navigate("/dashboard");
  }, [isLogged, navigate]);

  useEffect(() => {
    if (error) goldToast.error(error);
  }, [error]);

  useEffect(() => {
    if (successMessage && !error) goldToast.success(successMessage);
  }, [successMessage, error]);

  const clearStatusOnEdit = () => {
    if (error || successMessage) dispatch(clearError());
  };

  const handleLoginSubmit = (e) => {
    e.preventDefault();
    if (!username || !password) return;

    try {
      if (rememberMe) {
        localStorage.setItem("remember_username", username);
        localStorage.setItem("remember_me", "true");
      } else {
        localStorage.removeItem("remember_username");
        localStorage.removeItem("remember_me");
      }
    } catch {}

    dispatch(loginUser({ uniqueValue: username.trim(), password }));
  };

  const handle2FASubmit = (e) => {
    e.preventDefault();
    if (!code || code.length < 6) return;
    dispatch(verifyTwoFactor({ userId: tempUserId, code: code.trim() }));
  };

  const submitDisabled =
    loading || (!requiresTwoFactor && (!username || !password)) || (requiresTwoFactor && code.length < 6);

  return (
    <div className="flex min-h-screen w-full bg-white font-sans text-slate-800">
      <main className="flex flex-1 items-center justify-center p-5 sm:p-10">
        <div className="w-full max-w-sm" data-testid="login-window">
          <div className="mb-8 flex flex-col items-center text-center">
            <img
              src="/logo192.png"
              alt="Altınışık Logo"
              className="mb-4 h-14 w-14 rounded-2xl bg-slate-950 object-contain p-2"
            />
            <h1 className="text-2xl font-black tracking-tight text-slate-900">
              {requiresTwoFactor ? "Kimliğinizi doğrulayın" : "Giriş Yap"}
            </h1>
            {requiresTwoFactor && (
              <p className="mt-1 text-sm text-slate-500">Cihazınıza gönderilen doğrulama kodunu girin.</p>
            )}
          </div>

          <form onSubmit={requiresTwoFactor ? handle2FASubmit : handleLoginSubmit} className="flex flex-col gap-5">
            {!requiresTwoFactor ? (
              <>
                <div>
                  <FieldLabel htmlFor="login-username">Kullanıcı Adı veya E-Posta</FieldLabel>
                  <Input
                    id="login-username"
                    size="lg"
                    variant="filled"
                    icon={<User size={18} />}
                    placeholder="Kullanıcı adınızı girin"
                    value={username}
                    onChange={(e) => {
                      setUsername(e.target.value);
                      clearStatusOnEdit();
                    }}
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck="false"
                    autoComplete="username"
                    disabled={loading}
                    data-testid="login-username-input"
                  />
                </div>

                <div>
                  <FieldLabel htmlFor="login-password">Şifre</FieldLabel>
                  <Password
                    id="login-password"
                    size="lg"
                    variant="filled"
                    icon={<Lock size={18} />}
                    visibilityLabels={{
                      show: <Eye size={18} aria-label="Şifreyi Göster" />,
                      hide: <EyeOff size={18} aria-label="Şifreyi Gizle" />,
                    }}
                    placeholder="Şifrenizi girin"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      clearStatusOnEdit();
                    }}
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck="false"
                    autoComplete="current-password"
                    disabled={loading}
                    data-testid="login-password-input"
                  />
                </div>

                <Checkbox
                  label="Beni Hatırla"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
              </>
            ) : (
              <>
                <Alert
                  color="primary"
                  variant="filled"
                  size="sm"
                  icon={<ShieldCheck size={20} />}
                  title="İki Aşamalı Doğrulama (2FA)"
                  description="E-posta adresinize gönderilen güvenlik kodunu girin."
                />

                <div>
                  <FieldLabel htmlFor="login-2fa">Doğrulama Kodu</FieldLabel>
                  <Input
                    id="login-2fa"
                    size="lg"
                    variant="filled"
                    className="text-center font-mono !text-2xl font-bold tracking-[0.4em]"
                    placeholder="12345678"
                    maxLength={8}
                    value={code}
                    onChange={(e) => {
                      setCode(e.target.value.replace(/\D/g, ""));
                      clearStatusOnEdit();
                    }}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    autoComplete="one-time-code"
                    autoFocus
                    disabled={loading}
                    data-testid="login-2fa-input"
                  />
                </div>
              </>
            )}

            <Button
              type="submit"
              size="lg"
              radius="lg"
              color="dark"
              loading={loading}
              disabled={submitDisabled}
              className="!w-full"
              data-testid="login-submit-btn"
            >
              <span className="flex items-center justify-center gap-2">
                {requiresTwoFactor ? "Doğrula ve Giriş Yap" : "Giriş Yap"}
                <ArrowRight size={18} />
              </span>
            </Button>

            {requiresTwoFactor && (
              <Button type="button" variant="text" color="dark" className="!w-full" onClick={() => dispatch(clearError())}>
                <span className="flex items-center justify-center gap-1.5">
                  <ArrowLeft size={14} /> Kullanıcı Adı & Şifreye Dön
                </span>
              </Button>
            )}
          </form>
        </div>
      </main>
    </div>
  );
};

export default LoginPage;
